import { AlertCircle, AlertTriangle, Info } from "lucide-react";
import type { ComponentType, ReactNode, SVGProps } from "react";
import { cn } from "@cliently/ui";

export type InfoCalloutVariant = "neutral" | "info" | "warning" | "danger";

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

const VARIANT_STYLES: Record<
  InfoCalloutVariant,
  { container: string; icon: string; Icon: IconComponent }
> = {
  neutral: {
    container: "border-border bg-transparent text-muted-foreground",
    icon: "opacity-80",
    Icon: Info,
  },
  info: {
    container:
      "border-sky-500/25 bg-sky-500/5 text-sky-700 dark:text-sky-400",
    icon: "opacity-90",
    Icon: Info,
  },
  warning: {
    container:
      "border-amber-500/25 bg-amber-500/5 text-amber-700 dark:text-amber-400",
    icon: "opacity-90",
    Icon: AlertTriangle,
  },
  danger: {
    container: "border-destructive/25 bg-destructive/5 text-destructive",
    icon: "opacity-90",
    Icon: AlertCircle,
  },
};

export type InfoCalloutProps = {
  children: ReactNode;
  variant?: InfoCalloutVariant;
  icon?: IconComponent;
  className?: string;
};

export const InfoCallout = ({
  children,
  variant = "neutral",
  icon: IconOverride,
  className,
}: Readonly<InfoCalloutProps>) => {
  const { container, icon, Icon: IconDefault } = VARIANT_STYLES[variant];
  const Icon = IconOverride ?? IconDefault;

  return (
    <div
      className={cn(
        "flex gap-3 rounded-lg border p-4 text-sm",
        container,
        className,
      )}
    >
      <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", icon)} aria-hidden />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
};
